import { createContext, useCallback, useContext, useEffect, useMemo, useState, useRef } from "react";
import { makeStyles } from "tss-react/mui";
import { Box, Grid, Slide, Stack } from "@mui/material";
import Scrollbar from "./Scrollbar";
import LargeHeader from "../navigation/LargeHeader";
import { useDevice } from "../../../providers/DeviceProvider";
import { useScrollState } from "../../../hooks/useScrollState";
import { Helmet } from "react-helmet-async";
import { useNavState } from "../../../providers/NavStateProvider";

const useStyles = makeStyles()(
  (theme) => ({
    wideRoot: {
      padding: `${theme.spacing(2)} ${theme.spacing(4)}`,
      paddingBottom: "200px",
      display: "flex",
      justifyContent: "center",
      alignItems: "flex-start",
      overflowAnchor: "none",
      "@media (max-width: 1200px)": {
        padding: theme.spacing(2),
      }
    },
    scrollHeader: {
      position: "absolute",
      top: 0,
      width: "100%",
      zIndex: 101,
      padding: `${theme.spacing(1)} ${theme.spacing(2)}`,
      background: "white",
      boxShadow: "0px 0px 3px 0px rgba(0, 0, 0, 0.1)",
    },
    slimRoot: {
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      overflow: "hidden",
      background: theme.palette.background.default
    },
    slimScrollbar: {
      padding: `${theme.spacing(2)} calc(16px - var(--scrollbar-width))`,
      paddingBottom: theme.spacing(9),
      overflowAnchor: "none",
    },
    headerWidget: {
      position: "relative",
      boxShadow: "none",
      transition: "box-shadow 0.25s",
      zIndex: 8,
    },
    headerWidgetShadow: {
      boxShadow: "0px 3px 3px 0px rgba(0, 0, 0, 0.1)",
    },
    footerWidget: {
      padding: theme.spacing(2),
      height: "auto",
      width: "100%",
      borderTopLeftRadius: theme.spacing(2),
      borderTopRightRadius: theme.spacing(2),
      background: theme.palette.background.default,
      transition: "box-shadow 0.25s",
      zIndex: 10,
    },
    footerWidgetScroll: {
      boxShadow: "0px 0px 3px 0px rgba(0, 0, 0, 0.10)",
    },
  })
);

export const ResponsivePageContext = createContext();

export const useResponsivePage = () => useContext(ResponsivePageContext);

export default function ResponsivePage(props) {
  const { showTitleWhenScrolled, maxWidth, className, safeTitle, title, caption, stateTag, stateTagColor, referenceTag, primaryAction, secondaryAction, onPrimaryAction, onSecondaryAction, menuItems, backButton, backPath, headerWidget, footerWidget, secondaryActionComponent, secondaryActionIcon, widget, secondaryActionDisabled, primaryActionDisabled } = props;
  const { classes } = useStyles();
  const { slim } = useDevice();
  const [actionButton, setActionButton] = useState(null);
  const scrollPanelRef = useRef(null);

  const { stateEvents, registerScrollState } = useNavState();

  const onScroll = useCallback((scrollTop) => {
    registerScrollState(scrollTop, scrollPanelRef.current.scrollHeight);
  }, [registerScrollState]);

  const { scrollRef: scrollRefInternal, scrollTop, scrollBottom } = useScrollState(true, slim ? 0 : 100, onScroll);

  useEffect(() => {
    let timer = null;

    const fn = ({ scrollTop, scrollHeight }) => {
      const scrollcontent = scrollPanelRef.current;
      const scrollbar = scrollRefInternal.current;

      if (scrollTop > 0 && !!scrollcontent && !!scrollbar) {
        scrollcontent.style.minHeight = `${scrollHeight}px`;

        scrollbar.scrollTo({
          top: scrollTop,
          behavior: "instant",
        });

        // cleanup min-height after load
        timer = setTimeout(() => {
          scrollcontent.style.removeProperty("min-height");
        }, 2500);
      }
    };

    stateEvents.on("restoreScrollState", fn);

    return () => {
      clearTimeout(timer);
      stateEvents.off("restoreScrollState", fn);
    };
  }, [stateEvents])


  const maxWidthStyle = useMemo(() => {
    return !maxWidth ? undefined : { maxWidth };
  }, [maxWidth]);

  return (
    <ResponsivePageContext.Provider
      value={{
        setActionButton
      }}
    >
      {
        !!title && typeof title === "string" ? (
          <Helmet>
            <title>Assist - {title}</title>
          </Helmet>
        ) : !!safeTitle ? (
          <Helmet>
            <title>Assist - {safeTitle}</title>
          </Helmet>
        ) : null
      }
      {
        slim ? (
          <Stack className={`${classes.slimRoot} ${className || ""}`} direction="column">
            <LargeHeader
              shadow={!scrollTop && !headerWidget}
              widget={widget}
              title={!showTitleWhenScrolled || !scrollTop ? title : undefined}
              caption={caption}
              stateTag={stateTag}
              stateTagColor={stateTagColor}
              primaryAction={primaryAction}
              referenceTag={referenceTag}
              secondaryAction={secondaryAction}
              secondaryActionComponent={secondaryActionComponent}
              secondaryActionIcon={secondaryActionIcon}
              onPrimaryAction={onPrimaryAction}
              onSecondaryAction={onSecondaryAction}
              secondaryActionDisabled={secondaryActionDisabled}
              primaryActionDisabled={primaryActionDisabled}
              menuItems={menuItems}
              backButton={backButton}
              backPath={backPath}
              actionButton={actionButton}
            />
            {!!headerWidget ? (
              <div className={`${classes.headerWidget} ${!scrollTop ? classes.headerWidgetShadow : ""}`}>
                {headerWidget}
              </div>
            ) : null}

            <Scrollbar className={classes.slimScrollbar} scrollRef={scrollRefInternal}>
              {props.outerChildren}
              <Grid container direction="row" spacing={2} ref={scrollPanelRef} alignContent="flex-start">
                {props.children}
              </Grid>
            </Scrollbar>
            {!!footerWidget ? (
              <div className={`${classes.footerWidget} ${!scrollBottom ? classes.footerWidgetScroll : ""}`}>
                {footerWidget}
              </div>
            ) : null}
          </Stack>
        ) : (
          <>

            {
              backButton ? (
                <Slide in={!scrollTop}>
                  <Box className={classes.scrollHeader}>

                    <LargeHeader
                      compact
                      widget={widget}
                      title={title}
                      caption={caption}
                      referenceTag={referenceTag}
                      stateTag={stateTag}
                      stateTagColor={stateTagColor}
                      primaryAction={primaryAction}
                      secondaryAction={secondaryAction}
                      onPrimaryAction={onPrimaryAction}
                      onSecondaryAction={onSecondaryAction}
                      secondaryActionComponent={secondaryActionComponent}
                      secondaryActionIcon={secondaryActionIcon}
                      secondaryActionDisabled={secondaryActionDisabled}
                      primaryActionDisabled={primaryActionDisabled}
                      menuItems={menuItems}
                      backButton={backButton}
                      backPath={backPath}
                      actionButton={actionButton}
                    />

                  </Box>
                </Slide>
              ) : null
            }

            <Scrollbar className={`${classes.wideRoot} ${className || ""}`} scrollRef={scrollRefInternal}>
              <Grid
                container
                direction="row"
                spacing={3}
                alignItems="flex-start"
                justifyContent="flex-start"
                alignContent="flex-start"
                item
                style={maxWidthStyle}
                ref={scrollPanelRef}
              >

                <LargeHeader
                  widget={widget}
                  title={title}
                  referenceTag={referenceTag}
                  caption={caption}
                  stateTag={stateTag}
                  stateTagColor={stateTagColor}
                  primaryAction={primaryAction}
                  secondaryAction={secondaryAction}
                  onPrimaryAction={onPrimaryAction}
                  onSecondaryAction={onSecondaryAction}
                  secondaryActionComponent={secondaryActionComponent}
                  secondaryActionIcon={secondaryActionIcon}
                  secondaryActionDisabled={secondaryActionDisabled}
                  primaryActionDisabled={primaryActionDisabled}
                  menuItems={menuItems}
                  backButton={backButton}
                  backPath={backPath}
                  actionButton={actionButton}
                />

                {headerWidget}

                {props.children}

              </Grid>
            </Scrollbar>
          </>
        )
      }
    </ResponsivePageContext.Provider>
  );
}